import React, { useState, useMemo, useEffect } from 'react';
import { Trash2, X, AlertTriangle, Loader2, ChevronRight } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { primaryText, semiBoldText, baseTextSize, smallTextSize } from '../../../../styling/stylingHelper';
import { deleteBulkCostPriceData, fetchRevenueItemNames } from '../../../../services/costPrice/costPriceService';
import { useTranslation } from 'react-i18next';
import { DishRowData } from '../new/costPriceDataTypes';
import { RevenueSourceType } from '../../../../types/RevenueSourceType';

type DeleteScope = 'all' | 'food' | 'drinks' | 'unlinked';

interface CostPriceDeleteModalProps {
  restaurantId: string;
  dishData: DishRowData[];
  onClose: () => void;
  onSuccess: () => void;
}

const CostPriceDeleteModal: React.FC<CostPriceDeleteModalProps> = ({
  restaurantId,
  dishData,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();

  // Step 1: options, Step 2: confirm
  const [step, setStep] = useState<1 | 2>(1);
  const [deleteItems, setDeleteItems] = useState(true);
  const [deleteCategories, setDeleteCategories] = useState(false);
  const [deleteScope, setDeleteScope] = useState<DeleteScope>('all');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Revenue item names for the restaurant (used to detect analyses no longer
  // linked to any revenue item). Null while loading.
  const [revenueItemNames, setRevenueItemNames] = useState<string[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchRevenueItemNames(restaurantId)
      .then((names) => { if (!cancelled) setRevenueItemNames(names); })
      .catch(() => { if (!cancelled) setRevenueItemNames([]); });
    return () => { cancelled = true; };
  }, [restaurantId]);

  // Dishes whose menu item name matches no revenue item (includes custom items
  // that were never tied to revenue). Empty until revenue names have loaded.
  const unlinkedDishIds = useMemo(() => {
    if (!revenueItemNames) return [];
    const revenueSet = new Set(revenueItemNames.map(n => n.trim().toLowerCase()));
    return dishData
      .filter(d => !d.isPlaceholder)
      .filter(d => {
        const name = d.menuItemName?.trim().toLowerCase();
        return !name || !revenueSet.has(name);
      })
      .map(d => d.id);
  }, [revenueItemNames, dishData]);

  // Resolve item IDs based on scope
  const itemIdsToDelete = useMemo(() => {
    if (!deleteItems) return [];
    if (deleteScope === 'all') return undefined; // undefined = delete all items
    if (deleteScope === 'unlinked') return unlinkedDishIds;

    const sourceType = deleteScope === 'food' ? RevenueSourceType.FOOD : RevenueSourceType.DRINKS;
    return dishData
      .filter(d => d.revenueSourceType === sourceType && !d.isPlaceholder)
      .map(d => d.id);
  }, [deleteItems, deleteScope, dishData, unlinkedDishIds]);

  // Counts for the summary
  const counts = useMemo(() => {
    const totalItems = dishData.filter(d => !d.isPlaceholder).length;
    const foodItems = dishData.filter(d => d.revenueSourceType === RevenueSourceType.FOOD && !d.isPlaceholder).length;
    const drinkItems = dishData.filter(d => d.revenueSourceType === RevenueSourceType.DRINKS && !d.isPlaceholder).length;
    return { totalItems, foodItems, drinkItems, unlinkedItems: unlinkedDishIds.length };
  }, [dishData, unlinkedDishIds]);

  const affectedCount = deleteScope === 'all'
    ? counts.totalItems
    : deleteScope === 'food'
      ? counts.foodItems
      : deleteScope === 'drinks'
        ? counts.drinkItems
        : counts.unlinkedItems;

  // A filtered scope resolves to a list of ids; an empty list would be sent to
  // the backend which interprets "no ids" as delete-all, so require at least one
  // matched item before allowing item deletion in a filtered scope.
  const hasItemsToDelete = deleteItems && (deleteScope === 'all' || affectedCount > 0);
  const canProceed = hasItemsToDelete || deleteCategories;

  // Without the categories going too, the items are kept and only their
  // ingredient lists are emptied — deleting the rows would take the revenue
  // links and category assignments with them.
  const clearIngredientsOnly = hasItemsToDelete && !deleteCategories;

  const handleConfirm = async () => {
    setIsDeleting(true);
    setError(null);

    try {
      await deleteBulkCostPriceData(restaurantId, {
        // Only delete items when there is actually something to delete for the
        // selected scope — an empty id list would be read as delete-all.
        deleteItems: hasItemsToDelete,
        deleteCategories,
        itemIds: itemIdsToDelete,
        clearIngredientsOnly,
      });
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || t('costPrice.deleteModal.errorGeneric'));
      setIsDeleting(false);
    }
  };

  // Step 1: Options
  if (step === 1) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
        <div className="bg-background dark:bg-background-dark rounded-lg max-w-md w-full shadow-xl" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <h2 className={cn(primaryText, semiBoldText, baseTextSize)}>
                {t('costPrice.deleteModal.title')}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Options */}
          <div className="p-6 space-y-5">
            <p className={cn(primaryText, smallTextSize, "text-gray-600 dark:text-gray-400")}>
              {t('costPrice.deleteModal.selectOptions')}
            </p>

            {/* What to delete */}
            <div className="space-y-3">
              <label className={cn(primaryText, smallTextSize, semiBoldText)}>
                {t('costPrice.deleteModal.whatToDelete')}
              </label>

              {/* Delete items checkbox */}
              <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={deleteItems}
                  onChange={(e) => setDeleteItems(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 dark:border-borderPrimary-dark text-red-600 dark:text-red-500 focus:ring-red-500 dark:focus:ring-red-500"
                />
                <div>
                  <div className={cn(primaryText, smallTextSize, semiBoldText)}>
                    {deleteCategories
                      ? t('costPrice.deleteModal.costPriceItems')
                      : t('costPrice.deleteModal.costPriceItemsClear')}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {deleteCategories
                      ? t('costPrice.deleteModal.costPriceItemsDesc')
                      : t('costPrice.deleteModal.costPriceItemsClearDesc')}
                  </div>
                </div>
              </label>

              {/* Delete categories checkbox */}
              <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={deleteCategories}
                  onChange={(e) => setDeleteCategories(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 dark:border-borderPrimary-dark text-red-600 dark:text-red-500 focus:ring-red-500 dark:focus:ring-red-500"
                />
                <div>
                  <div className={cn(primaryText, smallTextSize, semiBoldText)}>
                    {t('costPrice.deleteModal.categories')}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {t('costPrice.deleteModal.categoriesDesc')}
                  </div>
                </div>
              </label>
            </div>

            {/* Scope filter (only when deleting items) */}
            {deleteItems && (
              <div className="space-y-3">
                <label className={cn(primaryText, smallTextSize, semiBoldText)}>
                  {t('costPrice.deleteModal.scope')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {(['all', 'food', 'drinks', 'unlinked'] as DeleteScope[]).map((scope) => {
                    const label = scope === 'all'
                      ? t('costPrice.deleteModal.scopeAll')
                      : scope === 'food'
                        ? t('costPrice.deleteModal.scopeFood')
                        : scope === 'drinks'
                          ? t('costPrice.deleteModal.scopeDrinks')
                          : t('costPrice.deleteModal.scopeUnlinked');
                    const count = scope === 'all'
                      ? counts.totalItems
                      : scope === 'food'
                        ? counts.foodItems
                        : scope === 'drinks'
                          ? counts.drinkItems
                          : counts.unlinkedItems;

                    return (
                      <button
                        key={scope}
                        onClick={() => setDeleteScope(scope)}
                        className={cn(
                          "flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors",
                          deleteScope === scope
                            ? "border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300"
                            : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                        )}
                      >
                        {label}
                        <span className="block text-xs font-normal mt-0.5">({count})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                {t('costPrice.deleteModal.cancel')}
              </button>
              <button
                onClick={() => setStep(2)}
                disabled={!canProceed}
                className="flex-1 px-6 py-3 bg-red-600 dark:bg-red-700 text-white dark:text-white rounded-lg font-medium hover:bg-red-700 dark:hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {t('costPrice.deleteModal.next')}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Confirm
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-background dark:bg-background-dark rounded-lg max-w-md w-full shadow-xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <h2 className={cn(primaryText, semiBoldText, baseTextSize)}>
              {t('costPrice.deleteModal.confirmTitle')}
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Confirm content */}
        <div className="p-6 space-y-4">
          {/* Summary of what will be deleted */}
          <div className="space-y-2">
            {hasItemsToDelete && (
              <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <Trash2 className="w-4 h-4 text-red-500 dark:text-red-400 flex-shrink-0" />
                <span>
                  {deleteScope === 'all'
                    ? t(clearIngredientsOnly ? 'costPrice.deleteModal.summaryAllItemsCleared' : 'costPrice.deleteModal.summaryAllItems', { count: affectedCount })
                    : deleteScope === 'food'
                      ? t(clearIngredientsOnly ? 'costPrice.deleteModal.summaryFoodItemsCleared' : 'costPrice.deleteModal.summaryFoodItems', { count: affectedCount })
                      : deleteScope === 'drinks'
                        ? t(clearIngredientsOnly ? 'costPrice.deleteModal.summaryDrinksItemsCleared' : 'costPrice.deleteModal.summaryDrinksItems', { count: affectedCount })
                        : t(clearIngredientsOnly ? 'costPrice.deleteModal.summaryUnlinkedItemsCleared' : 'costPrice.deleteModal.summaryUnlinkedItems', { count: affectedCount })
                  }
                </span>
              </div>
            )}
            {deleteCategories && (
              <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <Trash2 className="w-4 h-4 text-red-500 dark:text-red-400 flex-shrink-0" />
                <span>{t('costPrice.deleteModal.summaryCategories')}</span>
              </div>
            )}
          </div>

          {/* Warning */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <p className="text-sm text-yellow-800 dark:text-yellow-300 font-medium">
              {clearIngredientsOnly
                ? t('costPrice.deleteModal.warningClear')
                : t('costPrice.deleteModal.warning')}
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => { setStep(1); setError(null); }}
              disabled={isDeleting}
              className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              {t('costPrice.deleteModal.back')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={isDeleting}
              className="flex-1 px-6 py-3 bg-red-600 dark:bg-red-700 text-white dark:text-white rounded-lg font-medium hover:bg-red-700 dark:hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('costPrice.deleteModal.deleting')}
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  {t('costPrice.deleteModal.confirmDelete')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostPriceDeleteModal;
